import { ArrowRight, CircleHelp } from "lucide-react"
import React from "react"
import { Link, useLocation } from "react-router-dom"

import { hintHref, mountForPath } from "../../help/helpLinks.js"
import { HELP_HINTS, type HelpHintId } from "../../help/hints.js"
import { cn } from "../lib/utils.js"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover.js"

/**
 * A small "?" that opens one plain-language hint and a Learn more link to the
 * matching Help section (docs/help/HELP_SECTION_PLAN.md §6). Built on Popover,
 * not Tooltip, so it opens on tap in the field app too. The trigger is a real
 * button: focusable, Enter/Space opens, Escape closes (Radix).
 *
 *   <HelpHint id="pricebook.same-file" />
 *
 * `newTab`: Learn more opens Help in a new tab, for hints in a dialog or form
 * whose typed input would be lost by navigating away. Office tree only: an
 * installed field app has no tabs, so there it always navigates in place.
 */

export function HelpHint({ id, className, newTab = false }: { id: HelpHintId; className?: string; newTab?: boolean }) {
  const hint = HELP_HINTS[id];
  const { pathname } = useLocation();
  const inNewTab = newTab && mountForPath(pathname) === 'office';
  return (
    <span data-help={id} className="inline-flex align-middle">
      <Popover>
        <PopoverTrigger
          type="button"
          aria-label={`Help: ${hint.text.split('. ')[0]}`}
          // Hints sit inside clickable cards and headers; a tap here is only for the hint.
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <CircleHelp className="h-4 w-4" aria-hidden />
        </PopoverTrigger>
        <PopoverContent
          className="w-72 space-y-2 text-sm"
          onClick={(e) => e.stopPropagation()}
          // ui/popover drops focus on close; a keyboard user should land back on the "?".
          onCloseAutoFocus={() => {}}
        >
          <p>{hint.text}</p>
          <Link
            to={hintHref(hint, pathname)}
            {...(inNewTab ? { target: "_blank", rel: "noopener" } : {})}
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            Learn more <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </PopoverContent>
      </Popover>
    </span>
  );
}
